import {
  DrawerDescription,
  CurrencyFormatted,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerClose,
  Typography,
  Separator,
  FormInput,
  Loading,
  Drawer,
  Button,
  Label,
  Badge,
  Form,
} from '@ui/components';
import { PropsWithChildren, useCallback, useEffect, useState, FC } from 'react';
import { PaymentCard } from '@pages/cost-centers/components';
import { PayUAgreements } from '@shared/components/payu';
import { Success, Cancel } from '@ui/components/icons';
import { useLocation } from 'react-router-dom';
import { Masks } from '@helpers/constant';

import { useCostCenterAccountTopUpsDrawer } from './use-cost-center-account-top-ups-drawer';
import { WireTransferMethod } from './wire-transfer-method/wire-transfer-method';
import { PaymentCardMethod } from './payment-card-method/payment-card-method';

export const CostCenterAccountTopUpsDrawer: FC<PropsWithChildren<{ costCenterId: string }>> = ({
  children,
  costCenterId,
}) => {
  const location = useLocation();

  const { isLoading, cards, t, isSaveTopUpPending, payURef, topUpData, onSubmit, currency, form } =
    useCostCenterAccountTopUpsDrawer({ costCenterId });
  const [isOpen, setIsOpen] = useState(false);

  const [isWireTransferFormOpened, setIsWireTransferFormOpened] = useState(false);
  const existingCard = form.watch('existingCard.paymentMethodId');
  const newCardForm = form.watch('newCard');
  const amount = form.watch('amount');

  const choosePaymentMethod = useCallback(
    (paymentMethod: 'existingCard' | 'wireTransfer' | 'newCard', paymentMethodId?: string) => {
      form.reset({
        amount: form.getValues('amount'),
        existingCard: paymentMethod === 'existingCard' ? { paymentMethodId } : undefined,
        newCard: paymentMethod === 'newCard' ? { firstName: '', lastName: '' } : undefined,
      });

      setIsWireTransferFormOpened(paymentMethod === 'wireTransfer');
    },
    [form],
  );

  useEffect(() => {
    if (location.state?.isTopUpDrawerOpen) {
      setIsOpen(true);
    }
  }, [location.state]);

  return (
    <Drawer onOpenChange={setIsOpen} open={isOpen} handleOnly>
      <DrawerTrigger asChild>{children}</DrawerTrigger>
      <DrawerContent>
        <DrawerHeader className="flex justify-between">
          <DrawerTitle>
            <DrawerDescription>{t('COST_CENTER.ACCOUNT_TOP_UPS.TITLE')}</DrawerDescription>
          </DrawerTitle>
          <DrawerClose asChild>
            <Button variant="ghost" icon>
              <Cancel />
            </Button>
          </DrawerClose>
        </DrawerHeader>
        <div className="flex-1 overflow-y-auto p-4 -mt-4">
          <Loading isLoading={isLoading}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
              }}
              className="space-y-4"
            >
              <Form {...form}>
                <section className="flex flex-col gap-4">
                  <div className="flex items-center gap-4 justify-between">
                    <Label htmlFor="accountNumber">
                      <Typography variant="labelMediumBook">
                        {t('COST_CENTER.ACCOUNT_TOP_UPS.TOP_UP_AMOUNT')}
                      </Typography>
                    </Label>
                    <Badge variant="success" size="small">
                      <Success className="size-3" />
                      {t('COST_CENTER.ACCOUNT_TOP_UPS.TOP_UP_AMOUNT_BADGE')}
                    </Badge>
                  </div>
                  <div>
                    <FormInput
                      trailingIcon={{
                        position: 'right',
                        icon: (
                          <Typography className="text-content-weak" variant="labelSmallBold">
                            {currency}
                          </Typography>
                        ),
                      }}
                      componentProps={{
                        maskOptions: { mask: Masks.CURRENCY_FORMAT },
                        className:
                          '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                      }}
                      formControl={form.control}
                      name="amount"
                      size="small"
                    />
                  </div>
                  <Typography variant="labelMediumBook">
                    <>
                      {t('COST_CENTER.ACCOUNT_TOP_UPS.TOP_UP_AMOUNT_MESSAGE')} -{' '}
                      <Typography variant="labelMediumBold" element="span">
                        <CurrencyFormatted
                          value={topUpData?.suggestedTopUpAmount ?? 0}
                          currency={currency}
                        />
                      </Typography>
                    </>
                  </Typography>
                </section>

                <Separator />

                <section className="flex flex-col gap-5 mt-5">
                  <Typography variant="h200">
                    {t('COST_CENTER.ACCOUNT_TOP_UPS.LINKED_CARDS_TITLE')}
                  </Typography>
                  <div className="flex flex-col gap-4">
                    {cards.map((card) => (
                      // TODO: Add enum for card status
                      <PaymentCard
                        className={
                          existingCard === card.paymentMethodId ? 'border-primary-default' : ''
                        }
                        subtitle={`${t('COST_CENTER.ACCOUNT_DETAILS.VALID_DATE')}: ${card.cardExpirationMonth}/${card.cardExpirationYear}`}
                        onClick={() => choosePaymentMethod('existingCard', card.paymentMethodId)}
                        title={`${card.cardBrand || '-'} ${card.cardNumber || '-'}`}
                        icon={<img src={card.imageUrl} alt="card-brand" />}
                        disabled={card.paymentMethodId === 'INACTIVE'}
                        key={card.paymentMethodId}
                      >
                        {/* <Typography variant="labelSmallBook">{card.paymentMethodId}</Typography> */}
                      </PaymentCard>
                    ))}
                  </div>
                </section>

                <Separator />

                <section className="flex flex-col gap-5 mt-5">
                  <Typography variant="h200">
                    {t('COST_CENTER.ACCOUNT_TOP_UPS.PAYMENT_METHOD_TITLE')}
                  </Typography>
                  <div className="flex flex-col gap-4">
                    {!!topUpData?.payUID && (
                      <PaymentCardMethod
                        onClick={() => choosePaymentMethod('newCard')}
                        payUID={topUpData.payUID}
                        control={form.control}
                        open={!!newCardForm}
                        payURef={payURef}
                      />
                    )}
                    <WireTransferMethod
                      costCenterAccountNumber={topUpData?.costCenterAccountNumber ?? ''}
                      onClick={() => choosePaymentMethod('wireTransfer')}
                      open={isWireTransferFormOpened}
                    />
                  </div>
                </section>

                <section>{(!!existingCard || !!newCardForm) && <PayUAgreements />}</section>
              </Form>
            </form>
          </Loading>
        </div>

        <DrawerFooter className="space-y-4 gap-0">
          <div className="flex gap-2 items-center justify-between">
            <Typography variant="labelMediumBold">
              {t('COST_CENTER.ACCOUNT_TOP_UPS.TOTAL_BALANCE')}
            </Typography>
            <Typography variant="labelMediumBold">
              <CurrencyFormatted currency={currency} value={amount} />
            </Typography>
          </div>
          <Typography variant="labelSmallBook" className="text-weak">
            {t('COST_CENTER.ACCOUNT_TOP_UPS.TOP_UP_FEE_MESSAGE')}
          </Typography>
          {!isWireTransferFormOpened ? (
            <Button
              disabled={isSaveTopUpPending || (!existingCard && !newCardForm)}
              isLoading={isSaveTopUpPending}
              onClick={onSubmit}
              variant="primary"
              type="button"
              wide
            >
              {t('COST_CENTER.ACCOUNT_TOP_UPS.TOP_UP_BUTTON')}
            </Button>
          ) : (
            <DrawerClose asChild>
              <Button variant="primary" wide>
                {t('COST_CENTER.ACCOUNT_TOP_UPS.CLOSE_BUTTON')}
              </Button>
            </DrawerClose>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
